import { useEffect, useId, useState } from 'react';

import { Badge, Button, Divider } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, Select, Textarea } from '@ds/forms';
import { dataSimples } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { REGIMES, REGIMES_ESPECIAIS, documentoFormatado, situacaoDoCertificado } from '@/data/notasFiscais';
import type { AlvoFiscal, DadosDoEmitente, EmitenteFiscal } from '@/data/notasFiscais';

export interface ConfiguracaoFiscalModalProps {
    alvo: AlvoFiscal;
    /** A configuração atual, ou nula quando ainda não há. */
    emitente: EmitenteFiscal | null;
    onFechar: () => void;
    onMudou: (emitente: EmitenteFiscal | null) => void;
}

type Campos = Required<Omit<DadosDoEmitente, 'issRetido' | 'ambiente' | 'emitirAoReceber'>> & {
    issRetido: boolean;
    ambiente: 'homologacao' | 'producao';
    emitirAoReceber: boolean;
};

function camposDe(e: EmitenteFiscal | null): Campos {
    return {
        cnpj: e?.cnpj ? documentoFormatado(e.cnpj) : '', razaoSocial: e?.razaoSocial ?? '', nomeFantasia: e?.nomeFantasia ?? '',
        inscricaoMunicipal: e?.inscricaoMunicipal ?? '', regimeTributario: e?.regimeTributario ?? '', email: e?.email ?? '',
        cep: e?.cep ?? '', logradouro: e?.logradouro ?? '', numero: e?.numero ?? '', complemento: e?.complemento ?? '',
        bairro: e?.bairro ?? '', municipio: e?.municipio ?? '', codigoMunicipio: e?.codigoMunicipio ?? '', uf: e?.uf ?? '',
        itemListaServico: e?.itemListaServico ?? '', codigoTributacaoMunicipio: e?.codigoTributacaoMunicipio ?? '',
        cnae: e?.cnae ?? '', aliquotaIss: e?.aliquotaIss === null || e?.aliquotaIss === undefined ? '' : String(e.aliquotaIss).replace('.', ','),
        issRetido: e?.issRetido ?? false, descricaoPadrao: e?.descricaoPadrao ?? '', serieRps: e?.serieRps ?? '1',
        ambiente: e?.ambiente ?? 'homologacao', emitirAoReceber: e?.emitirAoReceber ?? false,
        codigoTributacaoNacional: e?.codigoTributacaoNacional ?? '', codigoNbs: e?.codigoNbs ?? '',
        ibsCbsCst: e?.ibsCbsCst ?? '', ibsCbsClassificacao: e?.ibsCbsClassificacao ?? '',
        regimeEspecial: String(e?.regimeEspecial ?? 0),
    };
}

const secao = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 } as const;

/**
 * Os dados fiscais de quem emite. O que falta aqui aparece como pendência em
 * cada nota; nada impede salvar pela metade. O certificado vai à borda, que o
 * abre com a senha antes de guardar — a tela nunca vê o arquivo de novo. "Ligar
 * ao emissor" cadastra a empresa no Focus NFe com esses dados e o certificado.
 */
export function ConfiguracaoFiscalModal({ alvo, emitente, onFechar, onMudou }: ConfiguracaoFiscalModalProps) {
    const aviso = useAviso();
    const id = useId();
    const [atual, setAtual] = useState(emitente);
    const [inicial, setInicial] = useState(() => camposDe(emitente));
    const [campos, setCampos] = useState(inicial);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [arquivo, setArquivo] = useState<File | null>(null);
    const [senha, setSenha] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [erroDoCertificado, setErroDoCertificado] = useState<string | null>(null);
    const [ligando, setLigando] = useState(false);
    const [erroDoEmissor, setErroDoEmissor] = useState<string | null>(null);

    useEffect(() => { setAtual(emitente); }, [emitente]);

    const mudar = <C extends keyof Campos>(c: C, v: Campos[C]) => { setCampos((x) => ({ ...x, [c]: v })); setErro(null); };
    const sujo = JSON.stringify(campos) !== JSON.stringify(inicial);

    const salvar = async (): Promise<boolean> => {
        setSalvando(true);
        try {
            /* Só o que mudou: campo intocado não sobrescreve o que outra pessoa salvou. */
            const mudou = Object.fromEntries(
                (Object.keys(campos) as (keyof Campos)[])
                    .filter((k) => campos[k] !== inicial[k])
                    .map((k) => [k, campos[k]]),
            ) as DadosDoEmitente;
            const salvo = await dados().salvarEmitenteFiscal(alvo, mudou);
            setAtual(salvo);
            const novos = camposDe(salvo);
            setInicial(novos);
            setCampos(novos);
            onMudou(salvo);
            aviso({ title: 'Configuração fiscal salva' });
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const enviarCertificado = async () => {
        if (!arquivo || !senha) return;
        setEnviando(true);
        setErroDoCertificado(null);
        try {
            const certificado = await dados().enviarCertificadoFiscal(alvo, arquivo, senha);
            const lido = await dados().emitenteFiscal(alvo);
            setAtual(lido);
            onMudou(lido);
            if (!campos.cnpj && certificado.cnpj) {
                const cnpj = documentoFormatado(certificado.cnpj);
                setCampos((x) => ({ ...x, cnpj }));
                setInicial((x) => ({ ...x, cnpj }));
            }
            setArquivo(null);
            setSenha('');
            aviso({ title: 'Certificado guardado', description: `Válido até ${dataSimples(certificado.validoAte.slice(0, 10))}` });
            if (certificado.avisoDoEmissor) setErroDoEmissor(`O certificado ficou guardado, mas o emissor não o recebeu: ${certificado.avisoDoEmissor}`);
        } catch (e) {
            setErroDoCertificado(e instanceof Error ? e.message : 'Não foi possível enviar o certificado.');
        } finally {
            setEnviando(false);
        }
    };

    const removerCertificado = async () => {
        try {
            await dados().removerCertificadoFiscal(alvo);
            const lido = await dados().emitenteFiscal(alvo);
            setAtual(lido);
            onMudou(lido);
            aviso({ title: 'Certificado removido', tone: 'warning' });
        } catch (e) {
            setErroDoCertificado(e instanceof Error ? e.message : 'Não foi possível remover o certificado.');
        }
    };

    const ligar = async () => {
        setLigando(true);
        setErroDoEmissor(null);
        try {
            const { padrao } = await dados().ligarEmissorFiscal(alvo);
            const lido = await dados().emitenteFiscal(alvo);
            setAtual(lido);
            onMudou(lido);
            aviso({
                title: 'Empresa ligada ao emissor',
                description: padrao === 'nacional' ? 'O município emite pelo padrão nacional.' : 'O município emite pelo sistema da prefeitura.',
            });
        } catch (e) {
            setErroDoEmissor(e instanceof Error ? e.message : 'Não foi possível ligar ao emissor.');
            const lido = await dados().emitenteFiscal(alvo).catch(() => null);
            if (lido) setAtual(lido);
        } finally {
            setLigando(false);
        }
    };

    const cert = situacaoDoCertificado(atual?.certificado ?? null);
    const emissor = atual?.emissor ?? null;
    const ligado = Boolean(atual?.provedor && emissor?.ligadoEm);
    const campo = (c: keyof Campos | 'arquivo' | 'senha') => `${id}-${c}`;

    return (
        <Modal
            open width={720} onClose={onFechar}
            title={alvo === 'plataforma' ? 'Dados fiscais da plataforma' : 'Dados fiscais da clínica'}
            description={alvo === 'plataforma' ? 'Quem emite a nota da mensalidade para as clínicas.' : 'Quem emite a nota de serviço para o paciente. Os códigos e a alíquota vêm do contador.'}
            dirty={sujo} onSave={salvar}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Fechar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!sujo} loading={salvando}>Salvar</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <Divider label="Empresa" spacing={0} />
                <div style={secao}>
                    <FormField label="CNPJ" htmlFor={campo('cnpj')}>
                        <Input id={campo('cnpj')} value={campos.cnpj} onChange={(v) => mudar('cnpj', v)} placeholder="00.000.000/0000-00" />
                    </FormField>
                    <FormField label="Inscrição municipal" htmlFor={campo('inscricaoMunicipal')}>
                        <Input id={campo('inscricaoMunicipal')} value={campos.inscricaoMunicipal} onChange={(v) => mudar('inscricaoMunicipal', v)} />
                    </FormField>
                    <FormField label="Razão social" htmlFor={campo('razaoSocial')} style={{ gridColumn: '1 / -1' }}>
                        <Input id={campo('razaoSocial')} value={campos.razaoSocial} onChange={(v) => mudar('razaoSocial', v)} />
                    </FormField>
                    <FormField label="Nome fantasia" htmlFor={campo('nomeFantasia')}>
                        <Input id={campo('nomeFantasia')} value={campos.nomeFantasia} onChange={(v) => mudar('nomeFantasia', v)} />
                    </FormField>
                    <FormField label="Regime tributário" htmlFor={campo('regimeTributario')}>
                        <Select
                            id={campo('regimeTributario')} value={campos.regimeTributario} placeholder="Escolha"
                            onChange={(v) => mudar('regimeTributario', v as Campos['regimeTributario'])}
                            options={REGIMES.map((r) => ({ value: r.valor, label: r.rotulo }))}
                        />
                    </FormField>
                    <FormField label="E-mail fiscal" htmlFor={campo('email')} hint="Onde a prefeitura e o emissor avisam" style={{ gridColumn: '1 / -1' }}>
                        <Input id={campo('email')} type="email" value={campos.email} onChange={(v) => mudar('email', v)} />
                    </FormField>
                </div>

                <Divider label="Endereço" spacing={0} />
                <div style={secao}>
                    <FormField label="CEP" htmlFor={campo('cep')}>
                        <Input id={campo('cep')} value={campos.cep} onChange={(v) => mudar('cep', v)} inputMode="numeric" placeholder="00000-000" />
                    </FormField>
                    <FormField label="Logradouro" htmlFor={campo('logradouro')}>
                        <Input id={campo('logradouro')} value={campos.logradouro} onChange={(v) => mudar('logradouro', v)} />
                    </FormField>
                    <FormField label="Número" htmlFor={campo('numero')}>
                        <Input id={campo('numero')} value={campos.numero} onChange={(v) => mudar('numero', v)} />
                    </FormField>
                    <FormField label="Complemento" htmlFor={campo('complemento')}>
                        <Input id={campo('complemento')} value={campos.complemento} onChange={(v) => mudar('complemento', v)} />
                    </FormField>
                    <FormField label="Bairro" htmlFor={campo('bairro')}>
                        <Input id={campo('bairro')} value={campos.bairro} onChange={(v) => mudar('bairro', v)} />
                    </FormField>
                    <FormField label="Município" htmlFor={campo('municipio')}>
                        <Input id={campo('municipio')} value={campos.municipio} onChange={(v) => mudar('municipio', v)} />
                    </FormField>
                    <FormField label="Código IBGE do município" htmlFor={campo('codigoMunicipio')} hint="Sete dígitos — São Paulo é 3550308">
                        <Input id={campo('codigoMunicipio')} value={campos.codigoMunicipio} onChange={(v) => mudar('codigoMunicipio', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="UF" htmlFor={campo('uf')}>
                        <Input id={campo('uf')} value={campos.uf} onChange={(v) => mudar('uf', v.toUpperCase().slice(0, 2))} />
                    </FormField>
                </div>

                <Divider label="Serviço padrão" spacing={0} />
                <div style={secao}>
                    <FormField label="Item da lista de serviços" htmlFor={campo('itemListaServico')} hint="LC 116, como 06.02">
                        <Input id={campo('itemListaServico')} value={campos.itemListaServico} onChange={(v) => mudar('itemListaServico', v)} />
                    </FormField>
                    <FormField label="Código de tributação do município" htmlFor={campo('codigoTributacaoMunicipio')} hint="Quando a prefeitura pede">
                        <Input id={campo('codigoTributacaoMunicipio')} value={campos.codigoTributacaoMunicipio} onChange={(v) => mudar('codigoTributacaoMunicipio', v)} />
                    </FormField>
                    <FormField label="CNAE" htmlFor={campo('cnae')}>
                        <Input id={campo('cnae')} value={campos.cnae} onChange={(v) => mudar('cnae', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="Alíquota de ISS (%)" htmlFor={campo('aliquotaIss')} hint="De 0 a 5">
                        <Input id={campo('aliquotaIss')} value={campos.aliquotaIss} onChange={(v) => mudar('aliquotaIss', v)} inputMode="decimal" placeholder="2,00" />
                    </FormField>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Checkbox checked={campos.issRetido} onChange={(v) => mudar('issRetido', v)} label="ISS retido pelo tomador" />
                    </div>
                    <FormField label="Descrição padrão" htmlFor={campo('descricaoPadrao')} hint="Vai no começo de cada nota, antes da descrição do título" style={{ gridColumn: '1 / -1' }}>
                        <Textarea id={campo('descricaoPadrao')} rows={2} value={campos.descricaoPadrao} onChange={(v) => mudar('descricaoPadrao', v)} />
                    </FormField>
                    <FormField label="Série do RPS" htmlFor={campo('serieRps')}>
                        <Input id={campo('serieRps')} value={campos.serieRps} onChange={(v) => mudar('serieRps', v)} />
                    </FormField>
                    <FormField label="Ambiente" htmlFor={campo('ambiente')} hint="Homologação é o teste da prefeitura: a nota não vale">
                        <Select
                            id={campo('ambiente')} value={campos.ambiente} onChange={(v) => mudar('ambiente', v as Campos['ambiente'])}
                            options={[{ value: 'homologacao', label: 'Homologação' }, { value: 'producao', label: 'Produção' }]}
                        />
                    </FormField>
                    {alvo === 'clinica' && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Checkbox
                                checked={campos.emitirAoReceber} onChange={(v) => mudar('emitirAoReceber', v)}
                                label="Emitir a nota sozinho quando o título for recebido"
                            />
                        </div>
                    )}
                </div>

                <Divider label="Padrão nacional e reforma tributária" spacing={0} />
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    Cidades no padrão nacional pedem o código de tributação nacional. Os códigos do IBS/CBS só vão na nota quando os dois estiverem preenchidos. Quem informa é o contador.
                </p>
                <div style={secao}>
                    <FormField label="Código de tributação nacional" htmlFor={campo('codigoTributacaoNacional')} hint="Seis dígitos, como 060201">
                        <Input id={campo('codigoTributacaoNacional')} value={campos.codigoTributacaoNacional} onChange={(v) => mudar('codigoTributacaoNacional', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="Código NBS" htmlFor={campo('codigoNbs')} hint="Nove dígitos">
                        <Input id={campo('codigoNbs')} value={campos.codigoNbs} onChange={(v) => mudar('codigoNbs', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="Situação tributária do IBS/CBS" htmlFor={campo('ibsCbsCst')} hint="Três dígitos, como 000">
                        <Input id={campo('ibsCbsCst')} value={campos.ibsCbsCst} onChange={(v) => mudar('ibsCbsCst', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="Classificação tributária do IBS/CBS" htmlFor={campo('ibsCbsClassificacao')} hint="Seis dígitos, como 000001">
                        <Input id={campo('ibsCbsClassificacao')} value={campos.ibsCbsClassificacao} onChange={(v) => mudar('ibsCbsClassificacao', v)} inputMode="numeric" />
                    </FormField>
                    <FormField label="Regime especial de tributação" htmlFor={campo('regimeEspecial')}>
                        <Select
                            id={campo('regimeEspecial')} value={campos.regimeEspecial} onChange={(v) => mudar('regimeEspecial', v)}
                            options={REGIMES_ESPECIAIS.map((r) => ({ value: String(r.valor), label: r.rotulo }))}
                        />
                    </FormField>
                </div>

                <Divider label="Certificado digital A1" spacing={0} />
                {atual?.certificado && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
                        <Badge
                            dot
                            tone={cert.tipo === 'vencido' ? 'danger' : cert.tipo === 'vencendo' ? 'warning' : 'success'}
                        >
                            {cert.tipo === 'vencido' ? 'Vencido' : cert.tipo === 'vencendo' ? `Vence em ${cert.dias} ${cert.dias === 1 ? 'dia' : 'dias'}` : 'Válido'}
                        </Badge>
                        <span style={{ flex: 1, minWidth: 200 }}>
                            <strong>{atual.certificado.titular}</strong>
                            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>
                                {atual.certificado.cnpj ? `CNPJ ${documentoFormatado(atual.certificado.cnpj)} · ` : ''}
                                até {dataSimples(atual.certificado.validoAte.slice(0, 10))}
                            </span>
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => { void removerCertificado(); }}>Remover</Button>
                    </div>
                )}
                {erroDoCertificado && <Alert tone="danger" title="O certificado não foi guardado" dismissible={false}>{erroDoCertificado}</Alert>}
                <div style={secao}>
                    <FormField label={atual?.certificado ? 'Trocar pelo arquivo' : 'Arquivo .pfx'} htmlFor={campo('arquivo')} hint="O certificado A1 da empresa (e-CNPJ)">
                        <input
                            id={campo('arquivo')} type="file" accept=".pfx,.p12,application/x-pkcs12"
                            onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setErroDoCertificado(null); }}
                            style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}
                        />
                    </FormField>
                    <FormField label="Senha do certificado" htmlFor={campo('senha')}>
                        <Input id={campo('senha')} type="password" autoComplete="new-password" value={senha} onChange={(v) => { setSenha(v); setErroDoCertificado(null); }} />
                    </FormField>
                </div>
                <div>
                    <Button variant="secondary" onClick={() => { void enviarCertificado(); }} disabled={!arquivo || !senha} loading={enviando}>
                        Enviar certificado
                    </Button>
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        O servidor abre o arquivo com a senha, confere validade e CNPJ, e guarda os dois cifrados. Depois disso, ninguém lê o arquivo pela tela.
                    </p>
                </div>

                <Divider label="Emissor de notas" spacing={0} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
                    <Badge dot tone={ligado ? (emissor?.desatualizado ? 'warning' : 'success') : 'neutral'}>
                        {ligado ? (emissor?.desatualizado ? 'Ligado, desatualizado' : 'Ligado') : 'Não ligado'}
                    </Badge>
                    <span style={{ flex: 1, minWidth: 200 }}>
                        <strong>Focus NFe</strong>
                        <span style={{ display: 'block', color: 'var(--text-secondary)' }}>
                            {ligado
                                ? `${emissor?.padrao === 'nacional' ? 'Padrão nacional' : 'Sistema da prefeitura'}${emissor?.cancelaPorApi === false ? ' · a prefeitura não cancela pelo sistema' : ''}${emissor?.ligadoEm ? ` · desde ${dataSimples(emissor.ligadoEm.slice(0, 10))}` : ''}`
                                : 'Salve os dados da empresa e envie o certificado; depois, ligue.'}
                        </span>
                    </span>
                    <Button size="sm" variant="secondary" onClick={() => { void ligar(); }} loading={ligando} disabled={sujo}>
                        {ligado ? 'Atualizar no emissor' : 'Ligar ao emissor'}
                    </Button>
                </div>
                {sujo && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>Salve as mudanças antes de ligar ou atualizar no emissor.</p>}
                {ligado && emissor?.desatualizado && !erroDoEmissor && (
                    <Alert tone="warning" dismissible={false}>Os dados da empresa mudaram depois de ligar. Use "Atualizar no emissor" para a nota sair com eles.</Alert>
                )}
                {(erroDoEmissor ?? emissor?.mensagem) && (
                    <Alert tone="danger" title="O emissor recusou" dismissible={false}>{erroDoEmissor ?? emissor?.mensagem}</Alert>
                )}
            </div>
        </Modal>
    );
}
